import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  FolderOpen, 
  Upload, 
  Download, 
  Trash2, 
  File, 
  FileText, 
  Image, 
  Shield,
  Cloud,
  CloudOff,
  RefreshCw,
  Search,
  Calendar,
  HardDrive,
  FileKey,
  Building2,
  Filter,
  MoreVertical,
  Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// File type icons and labels
const FILE_TYPE_CONFIG = {
  certificates: { 
    icon: FileKey, 
    label: 'Certificados', 
    color: 'bg-amber-100 text-amber-600 dark:bg-amber-900 dark:text-amber-400',
    accept: '.pfx,.p12',
    description: 'Certificados digitais A1 (.pfx, .p12)'
  },
  logos: { 
    icon: Building2, 
    label: 'Logos', 
    color: 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400',
    accept: '.jpg,.jpeg,.png,.webp,.gif',
    description: 'Logos da empresa (.jpg, .png, .webp)'
  },
  documents: { 
    icon: FileText, 
    label: 'Documentos', 
    color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900 dark:text-emerald-400',
    accept: '.pdf,.doc,.docx,.xlsx,.xls',
    description: 'Documentos gerais (.pdf, .xlsx, .docx)'
  }
};

const Files = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storageStatus, setStorageStatus] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedType, setSelectedType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadFileType, setUploadFileType] = useState('documents');
  const [previewFile, setPreviewFile] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [filesRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/files`),
        axios.get(`${API_URL}/storage/status`)
      ]);
      setFiles(filesRes.data);
      setStorageStatus(statusRes.data);
    } catch (error) {
      console.error('Erro ao carregar arquivos:', error);
      toast.error('Erro ao carregar arquivos');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API_URL}/upload/${uploadFileType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Arquivo enviado com sucesso!');
      setUploadOpen(false);
      fetchData();
    } catch (error) {
      console.error('Erro no upload:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar arquivo');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await axios.get(`${API_URL}/files/${file.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Download iniciado!');
    } catch (error) {
      console.error('Erro no download:', error);
      toast.error('Erro ao baixar arquivo');
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Tem certeza que deseja excluir este arquivo?')) return;
    
    try {
      await axios.delete(`${API_URL}/files/${fileId}`);
      toast.success('Arquivo excluído!');
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast.error('Erro ao excluir arquivo');
    }
  };

  const handlePreview = async (file) => {
    if (file.content_type?.startsWith('image/')) {
      try {
        const response = await axios.get(`${API_URL}/files/${file.id}/download`, {
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        setPreviewFile({ ...file, previewUrl: url });
      } catch (error) {
        toast.error('Erro ao carregar preview');
      }
    } else {
      handleDownload(file);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (file) => {
    const config = FILE_TYPE_CONFIG[file.file_type];
    if (config) {
      const IconComponent = config.icon;
      return <IconComponent size={24} />;
    }
    
    if (file.content_type?.startsWith('image/')) {
      return <Image size={24} />;
    }
    return <File size={24} />;
  };

  const getFileTypeBadge = (fileType) => {
    const config = FILE_TYPE_CONFIG[fileType];
    if (!config) return <Badge variant="outline">{fileType}</Badge>;
    
    return (
      <Badge className={config.color}>
        {config.label}
      </Badge>
    );
  };

  // Filter files
  const filteredFiles = files.filter(file => {
    const matchesType = selectedType === 'all' || file.file_type === selectedType;
    const matchesSearch = !searchTerm || 
      file.original_filename?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesSearch;
  });

  // Stats by type
  const stats = {
    total: files.length,
    certificates: files.filter(f => f.file_type === 'certificates').length,
    logos: files.filter(f => f.file_type === 'logos').length,
    documents: files.filter(f => f.file_type === 'documents').length,
    totalSize: files.reduce((acc, f) => acc + (f.size || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-emerald-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="files-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
            Arquivos na Nuvem
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Gerencie certificados, documentos e logos da empresa
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Storage Status Badge */}
          <Badge 
            className={storageStatus?.available 
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300' 
              : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
            }
          >
            {storageStatus?.available ? (
              <><Cloud size={14} className="mr-1" /> Conectado</>
            ) : (
              <><CloudOff size={14} className="mr-1" /> Offline</>
            )}
          </Badge>
          
          <Button 
            onClick={() => setUploadOpen(true)}
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
            disabled={!storageStatus?.available}
            data-testid="upload-file-button"
          >
            <Upload size={20} className="mr-2" />
            Enviar Arquivo
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded-lg">
              <FolderOpen size={20} className="text-slate-600 dark:text-slate-400" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Total</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="bg-amber-100 dark:bg-amber-900 p-2 rounded-lg">
              <FileKey size={20} className="text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Certificados</p>
              <p className="text-2xl font-bold">{stats.certificates}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 dark:bg-blue-900 p-2 rounded-lg">
              <Building2 size={20} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Logos</p>
              <p className="text-2xl font-bold">{stats.logos}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-100 dark:bg-emerald-900 p-2 rounded-lg">
              <FileText size={20} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Documentos</p>
              <p className="text-2xl font-bold">{stats.documents}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 dark:bg-purple-900 p-2 rounded-lg">
              <HardDrive size={20} className="text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Tamanho Total</p>
              <p className="text-2xl font-bold">{formatFileSize(stats.totalSize)}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Buscar por nome do arquivo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-files-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-slate-500" />
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filtrar por tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                <SelectItem value="certificates">Certificados</SelectItem>
                <SelectItem value="logos">Logos</SelectItem>
                <SelectItem value="documents">Documentos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw size={16} className="mr-2" />
            Atualizar
          </Button>
        </div>
      </Card>

      {/* Files List */}
      <Card>
        {filteredFiles.length === 0 ? (
          <div className="p-12 text-center">
            <FolderOpen size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-4" />
            <p className="text-slate-600 dark:text-slate-400 mb-2">
              {files.length === 0 
                ? 'Nenhum arquivo enviado ainda'
                : 'Nenhum arquivo encontrado com os filtros selecionados'
              }
            </p>
            {files.length === 0 && (
              <Button 
                onClick={() => setUploadOpen(true)} 
                variant="outline"
                className="mt-2"
              >
                <Upload size={16} className="mr-2" />
                Enviar primeiro arquivo
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-800 border-b">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-slate-600 dark:text-slate-400">Arquivo</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600 dark:text-slate-400">Tipo</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600 dark:text-slate-400">Tamanho</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600 dark:text-slate-400">Data Upload</th>
                  <th className="text-center p-4 text-sm font-medium text-slate-600 dark:text-slate-400">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {filteredFiles.map((file) => (
                  <tr 
                    key={file.id} 
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                    data-testid={`file-row-${file.id}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${FILE_TYPE_CONFIG[file.file_type]?.color || 'bg-slate-100'}`}>
                          {getFileIcon(file)}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white truncate max-w-xs">
                            {file.original_filename}
                          </p>
                          <p className="text-xs text-slate-500 truncate max-w-xs">
                            {file.storage_path}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      {getFileTypeBadge(file.file_type)}
                    </td>
                    <td className="p-4 text-sm text-slate-600 dark:text-slate-400">
                      {formatFileSize(file.size)}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                        <Calendar size={14} />
                        {formatDate(file.uploaded_at)}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-center gap-2">
                        {file.content_type?.startsWith('image/') && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handlePreview(file)}
                            title="Visualizar"
                          >
                            <Eye size={16} />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDownload(file)}
                          title="Download"
                          data-testid={`download-${file.id}`}
                        >
                          <Download size={16} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDelete(file.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          title="Excluir"
                          data-testid={`delete-${file.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Upload Modal */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Enviar Arquivo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tipo de Arquivo</Label>
              <Select value={uploadFileType} onValueChange={setUploadFileType}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(FILE_TYPE_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <config.icon size={16} />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                {FILE_TYPE_CONFIG[uploadFileType]?.description}
              </p>
            </div>
            
            <div>
              <Label>Selecione o Arquivo</Label>
              <div className="mt-2 border-2 border-dashed rounded-lg p-8 text-center hover:border-emerald-400 transition-colors">
                <input
                  type="file"
                  onChange={handleUpload}
                  accept={FILE_TYPE_CONFIG[uploadFileType]?.accept}
                  className="hidden"
                  id="file-upload"
                  disabled={uploading}
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload size={32} className="mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {uploading ? (
                      <span className="flex items-center justify-center gap-2">
                        <RefreshCw size={16} className="animate-spin" />
                        Enviando...
                      </span>
                    ) : (
                      <>Clique para selecionar ou arraste o arquivo</>
                    )}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Aceita: {FILE_TYPE_CONFIG[uploadFileType]?.accept}
                  </p>
                </label>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={!!previewFile} onOpenChange={() => {
        if (previewFile?.previewUrl) {
          window.URL.revokeObjectURL(previewFile.previewUrl);
        }
        setPreviewFile(null);
      }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{previewFile?.original_filename}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-4">
            {previewFile?.previewUrl && (
              <img 
                src={previewFile.previewUrl} 
                alt={previewFile.original_filename}
                className="max-w-full max-h-[60vh] object-contain rounded-lg"
              />
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPreviewFile(null)}>
              Fechar
            </Button>
            <Button onClick={() => handleDownload(previewFile)}>
              <Download size={16} className="mr-2" />
              Download
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Files;
